import { CustomSelect } from '@/components/form/CustomSelect';
import { TutorsMultiSelect } from '@/components/form/TutorsMultiSelect';
import { CustomDatePicker } from '@/components/form/CustomDatePicker';
import { formatPhoneInput } from '@/lib/phoneMask';
import { STATUS_LABEL, TARIFF_LABEL, TYPE_LABEL, autoValidityForStatus } from '@/lib/clientFormat';
import type { ClientEditOptions } from '@/api/clients';
import { sanitizeMt1Login, type WizardState } from './wizardState';

/**
 * Шаг 1 — 1:1 поля Orchid-создания (CustomerEditLayout/CustomerAdminLayout,
 * см. докблок CustomerController::store() в Laravel), плюс mt1Login и email —
 * задаются только здесь (после создания не редактируются, см.
 * ClientDetailPage.tsx/GeneralEditForm).
 */
export function StepCompanyInfo({
  state,
  onChange,
  editOptions,
  fieldErrors,
}: {
  state: WizardState;
  onChange: (state: WizardState) => void;
  editOptions: ClientEditOptions | null;
  fieldErrors: string[];
}) {
  const set = <K extends keyof WizardState>(key: K, value: WizardState[K]) => {
    onChange({ ...state, [key]: value });
  };
  const invalid = (field: string) => (fieldErrors.includes(field) ? ' edit-field--invalid' : '');

  return (
    <div className="detail__grid-2">
      <div className="card">
        <h2 className="detail__subtitle">Информация о компании</h2>
        <div className="edit-fields">
          <label className={`edit-field${invalid('name')}`}>
            <span>Наименование компании *</span>
            <input type="text" value={state.name} onChange={(e) => set('name', e.target.value)} />
          </label>

          <label className={`edit-field${invalid('mt1Login')}`}>
            <span>Наименование кабинета в Maintest 5i *</span>
            <input
              type="text"
              value={state.mt1Login}
              onChange={(e) => set('mt1Login', sanitizeMt1Login(e.target.value))}
            />
          </label>

          <label className="edit-field edit-field--checkbox edit-field--full">
            <input
              type="checkbox"
              checked={state.isMt1HallAllowed}
              onChange={(e) => set('isMt1HallAllowed', e.target.checked)}
            />
            <span>Разрешить вход в Maintest-5i через персональный холл</span>
          </label>

          <label className="edit-field edit-field--full">
            <span>Кураторы</span>
            <TutorsMultiSelect
              tutorIds={state.tutorIds}
              tutors={editOptions?.tutors}
              disabled={!editOptions}
              onChange={(ids) => set('tutorIds', ids)}
            />
          </label>

          <label className="edit-field">
            <span>Тип *</span>
            <CustomSelect
              tooltip="Тип"
              value={state.type}
              options={Object.entries(TYPE_LABEL).map(([value, label]) => ({ value, label }))}
              onChange={(value) => set('type', value)}
            />
          </label>

          <label className="edit-field">
            <span>Тип тарификации *</span>
            <CustomSelect
              tooltip="Тип тарификации"
              value={state.tariffType}
              options={Object.entries(TARIFF_LABEL).map(([value, label]) => ({ value, label }))}
              onChange={(value) => set('tariffType', value)}
            />
          </label>

          <label className="edit-field">
            <span>Статус аккаунта *</span>
            <CustomSelect
              tooltip="Статус аккаунта"
              value={state.status}
              options={Object.entries(STATUS_LABEL).map(([value, label]) => ({ value, label }))}
              onChange={(status) => {
                onChange({ ...state, status, validity: autoValidityForStatus(status) });
              }}
            />
          </label>

          <label className="edit-field">
            <span>Дата отключения кабинета</span>
            <CustomDatePicker
              value={state.validity}
              placeholder="Бессрочно"
              onChange={(value) => set('validity', value)}
            />
            <span className="edit-field__hint">Оставьте пустым для неограниченного срока действия</span>
          </label>
        </div>

        <div className="detail__divider" />
        <h2 className="detail__subtitle detail__subtitle--sm">Заметки</h2>
        <textarea
          className="edit-field__textarea"
          rows={3}
          value={state.notes}
          onChange={(e) => set('notes', e.target.value)}
        />
      </div>

      <div className="card">
        <h2 className="detail__subtitle">Основной контакт</h2>
        <div className="edit-fields">
          <label className="edit-field">
            <span>Имя</span>
            <input type="text" value={state.firstName} onChange={(e) => set('firstName', e.target.value)} />
          </label>
          <label className="edit-field">
            <span>Фамилия</span>
            <input type="text" value={state.lastName} onChange={(e) => set('lastName', e.target.value)} />
          </label>
          <label className="edit-field">
            <span>Отчество</span>
            <input type="text" value={state.middleName} onChange={(e) => set('middleName', e.target.value)} />
          </label>
          <label className="edit-field">
            <span>Должность</span>
            <input type="text" value={state.position} onChange={(e) => set('position', e.target.value)} />
          </label>
          <label className={`edit-field${invalid('email')}`}>
            <span>Email *</span>
            <input type="email" value={state.email} onChange={(e) => set('email', e.target.value)} />
          </label>
          <label className="edit-field">
            <span>Телефон</span>
            <input
              type="tel"
              placeholder="+7 (___) ___-__-__"
              value={state.phone}
              onChange={(e) => set('phone', formatPhoneInput(e.target.value))}
            />
          </label>
        </div>
      </div>
    </div>
  );
}
